import React, { useState, useEffect } from 'react';
import { array, bool, number } from 'prop-types';
import { isEmpty, get, uniq } from 'lodash';

import MaterialTable from 'material-table';
import CircularProgress from '@material-ui/core/CircularProgress';

import { TablePagination } from '@material-ui/core';

import template from 'url-template';

import lambdaBaseApi from '../../apis/lambda';
import awsHeader from '../../helpers/awsAuth';

import clsx from 'clsx';

import ConfirmButton from '../../components/ConfirmButton';
import NoDataSadFace from '../../components/NoDataSadFace';
import ActionBar from '../../components/ActionBar';
import ActionBarItem from '../../components/ActionBarItem';
import Switch from '../../components/FormFields/Switch';

import classes from './CustomersTable.module.css';
import { noData, noDataContainer, noDataButton } from './DevicesObjectsPanel.module.css';

const ErpContactsUrl = template.parse('/externalcontacts{?externalSiteId}');
const ErpSitesUrl = template.parse('/externalsites{?externalClientId}');
const ErpEquipmentUrl = template.parse('/externalequipments{?externalSiteId}');

const LocationTable = ({rowData, renderProgress}) => {
    const [erpSites, setErpSites] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        async function init() {
            const header = await awsHeader();
            let response;
            setIsLoading(true);
            try {
                response = await lambdaBaseApi.get(
                    ErpSitesUrl.expand({
                        externalClientId: rowData.id
                    }),
                    header
                );
            } catch (error) {
                return [];
            }
            let newData = get(response, 'data.data', []);
            setErpSites(newData);
            setIsLoading(false);
        }
        init();

    }, []
    );

    if (isLoading) {
        return renderProgress();
    }

    return (
        <div className={classes.subTable}>
            <MaterialTable
                options={{
                    paging:    false,
                    showTitle: false,
                    search:    false,
                    toolbar:   false,
                    selection: false
                }}
                columns={[
                    { title: 'Location ID', field: 'sourceId' },
                    { title: 'Name', field: 'name' },
                    { title: 'Address', field: 'address.formattedAddress' }
                ]}
                data={erpSites}
                detailPanel={rowData => (
                    <ChildTables rowData={rowData} renderProgress={renderProgress} />
                )}
            />
        </div>
    );
};

const ChildTables = ({rowData, renderProgress}) => {
    const [erpEquipment, setErpEquipment] = useState([]);
    const [erpContacts, setErpContacts] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        async function init() {
            const header = await awsHeader();
            let contactResponse, equipmentResponse;
            setIsLoading(true);
            try {
                contactResponse = await lambdaBaseApi.get(
                    ErpContactsUrl.expand({
                        externalSiteId: rowData.id
                    }),
                    header
                );

                equipmentResponse = await lambdaBaseApi.get(
                    ErpEquipmentUrl.expand({
                        externalSiteId: rowData.id
                    }),
                    header
                );
            } catch (error) {
                return [];
            }

            const contactPayload = get(contactResponse, 'data.data', []);
            const equipmentPayload = get(equipmentResponse, 'data.data', []);

            setErpContacts(contactPayload);
            setErpEquipment(equipmentPayload);
            setIsLoading(false);
        }
        init();

    }, []
    );

    if (isLoading) {
        return renderProgress();
    }

    return (
        <div className={classes.subTable}>
            <ContactTable rowData={erpContacts} />
            <EquipmentTable rowData={erpEquipment} />
        </div>
    );
};

const ContactTable = ({rowData}) => {
    return (
        <div className={clsx(classes.subTable, classes.childTable)}>
            <MaterialTable
                options={{
                    paging:    false,
                    showTitle: false,
                    search:    false,
                    toolbar:   false
                }}
                columns={[
                    { title: 'Contact ID', field: 'sourceId' },
                    { title: 'Name', field: 'name' },
                    { title: 'Title', field: 'title' },
                    { title: 'Email', field: 'email' }
                ]}
                data={rowData}
            />
        </div>
    );
};


const EquipmentTable = ({rowData}) => {
    return (
        <div className={clsx(classes.subTable, classes.childTable)}>
            <MaterialTable
                options={{
                    paging:    false,
                    showTitle: false,
                    search:    false,
                    toolbar:   false
                }}
                columns={[
                    { title: 'Equipment ID', field: 'sourceId' },
                    { title: 'Name', field: 'name' },
                    { title: 'Type', field: 'equipmentType.name' }
                ]}
                data={rowData}
            />
        </div>
    );
};

const CustomersTable = ({
    customers,
    customersLoading,
    customersMeta,
    customerSearchValue,
    customerTrackingLoading,
    handleConfigure,
    isLoading,
    onPageSet,
    onPageSizeSet,
    page,
    pageSize,
    selectedAgent,
    setCustomerSearch,
    updateErpClient
}) => {

    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [customerToggles, setCustomerToggles] = useState({});
    const [totalCount, setTotalCount] = useState(0);
    const [toggledClients, setToggledClients] = useState([]);

    useEffect(() => {
        setToggledClients(selectedAgent.filteredClients);
    }, [selectedAgent]);

    useEffect(() => {
        customers.forEach(customer => {
            setCustomerToggles({...customerToggles, [customer.id]: false});
        });
    }, []);

    useEffect(() => {
        if (customersMeta.totalCount && customersMeta.totalCount !== totalCount) {
            setTotalCount(customersMeta.totalCount);
        }
    }, [customersMeta]);

    const nameSorter = (a, b) => {
        return sorter(a, b, 'name');
    };

    const customerIdSorter = (a, b) => {
        return sorter(a, b, 'sourceId');
    };

    const trackedSorter = (a, b) => {
        const aTracked = selectedAgent.filteredClients.includes(a.id);
        const bTracked = selectedAgent.filteredClients.includes(b.id);

        if (aTracked < bTracked) {
            return -1;
        }
        if (aTracked > bTracked) {
            return 1;
        }

        return 0;
    };

    const sorter = (a, b, property) => {
        const valueA = a[property].toUpperCase();
        const valueB = b[property].toUpperCase();

        if (valueA < valueB) {
            return -1;
        }
        if (valueA > valueB) {
            return 1;
        }

        return 0;
    };

    const handlePageChange = (event, page) => {
        onPageSet(page);
    };

    const handlePageSizeChange = event => {
        const { value } = event.target;

        onPageSizeSet(value);
    };

    const handleSearch = searchValue => {
        setCustomerSearch(searchValue);
    };

    const renderProgress = () => {
        return (
            <div className={classes.loadingIndicator}>
                <CircularProgress
                    size={25}
                />
            </div>
        );
    };

    const renderCopy = (rowData, value) => {
        if (rowData.loadFailed) {
            return (
                <span className={classes.error}>{rowData[value]}</span>
            );
        }

        return rowData[value];
    };

    const renderNoData = () => {
        return (
            <div className={noDataContainer}>
                <div className={noData}>
                    <NoDataSadFace />
                    <ConfirmButton
                        confirm={handleConfigure}
                        label="Configure FSM Integration"
                        className={noDataButton}
                    />
                </div>
            </div>
        );
    };

    const renderFullTable = () => {
        const handleSetSelectedCustomers = (selected) => {
            setSelectedCustomers(selected);
        };

        const handleStartTracking = async () => {
            if (isEmpty(selectedCustomers)) {
                return;
            }

            const alreadyTracked = customers.map(customer => toggledClients.find(id => id === customer.id)).filter(Boolean);
            const newIds = selectedCustomers.map(customer => customer.id);
            const idsToTrack = [...alreadyTracked, ...newIds];
            const dedupedIds = uniq(idsToTrack);
            const payload = Array.from(dedupedIds);

            const toggles = {};
            newIds.forEach(id => {
                toggles[id] = true;
            });
            setCustomerToggles({...customerToggles, ...toggles});

            await updateErpClient({ filteredClients: payload, id: selectedAgent.id });
            setToggledClients(payload);

            newIds.forEach(id => {
                toggles[id] = false;
            });
            setCustomerToggles({ ...customerToggles, ...toggles });
        };

        const handleStopTracking = async () => {
            if (isEmpty(selectedCustomers)) {
                return;
            }

            const customerIds = customers.map(customer => customer.id);
            const selectedIds = selectedCustomers.map(customer => customer.id);
            const idsToTrack = customerIds.filter(id => !selectedIds.find(selectedId => id === selectedId));
            const alreadyTracked = customers.map(customer => toggledClients.find(id => id === customer.id)).filter(Boolean);
            const dedupedIds = uniq(idsToTrack);
            const uniqueList = Array.from(dedupedIds);
            const payload = uniqueList.map(id => alreadyTracked.find(trackedId => trackedId === id)).filter(Boolean);

            const toggles = {};
            selectedIds.forEach(id => {
                toggles[id] = true;
            });
            setCustomerToggles({...customerToggles, ...toggles});

            await updateErpClient({ filteredClients: payload, id: selectedAgent.id });
            setToggledClients(payload);

            selectedIds.forEach(id => {
                toggles[id] = false;
            });
            setCustomerToggles({ ...customerToggles, ...toggles });
        };

        const handleCellToggle = async rowData => {
            let filteredClients = [];

            if (toggledClients && toggledClients.includes(rowData.id)) {
                filteredClients = toggledClients.filter(id => id !== rowData.id);
            }
            else if (toggledClients) {
                filteredClients = [...toggledClients];
                filteredClients.push(rowData.id);
            }

            setCustomerToggles({...customerToggles, [rowData.id]: true});
            await updateErpClient({ filteredClients, id: selectedAgent.id });
            setToggledClients(filteredClients);
            setCustomerToggles({...customerToggles, [rowData.id]: false});
        };

        const renderToggleCell = (rowData, value) => {
            if (rowData.loadFailed) {
                return (
                    <span className={classes.error}>{rowData[value]}</span>
                );
            }

            let isEnabled = false;
            if (toggledClients && rowData.id) {
                isEnabled = toggledClients.includes(rowData.id);
            }

            return (
                <Switch
                    checked={isEnabled}
                    disableRipple
                    name="enabled"
                    onChange={() => handleCellToggle(rowData)}
                    isLoading={customerToggles[rowData.id]}
                />
            );
        };

        if (!customersLoading && isEmpty(customers) && isEmpty(customerSearchValue)) {
            return renderNoData();
        }

        return (
            <React.Fragment>
                <div className={classes.CustomersTable} >
                    <ActionBar
                        onSearch={handleSearch}
                    >
                        <ActionBarItem
                            onClick={handleStartTracking}
                            iconSrc="/assets/images/switchEnabledIconColor.svg"
                        >
                            Start Tracking
                        </ActionBarItem>
                        <ActionBarItem
                            onClick={handleStopTracking}
                            iconSrc="/assets/images/switchDisabledIconColor.svg"
                        >
                            Stop Tracking
                        </ActionBarItem>
                    </ActionBar>
                    <MaterialTable
                        options={{
                            paging:              true,
                            showTitle:           false,
                            search:              false,
                            selection:           true,
                            sorting:             true,
                            toolbar:             false,
                            emptyRowsWhenPaging: false,
                            pageSize
                        }}
                        columns={[
                            {
                                title:      'Customer ID',
                                render:     rowData => renderCopy(rowData, 'sourceId'),
                                customSort: customerIdSorter,
                            },
                            {
                                title:       'Name',
                                render:      rowData => renderCopy(rowData, 'name'),
                                customSort:  nameSorter,
                                defaultSort: 'asc'
                            },
                            {
                                title: 'Address',
                                field: 'address.formattedAddress'
                            },
                            {
                                title:      'Tracked',
                                render:     rowData => renderToggleCell(rowData, 'tracked'),
                                customSort: trackedSorter
                            }
                        ]}
                        data={customers}
                        isLoading={isLoading}
                        detailPanel={rowData => <LocationTable rowData={rowData} renderProgress={renderProgress} />}
                        onSelectionChange={handleSetSelectedCustomers}
                        components={{
                            Pagination: props => {
                                return (
                                    <TablePagination
                                        {...props}
                                        count={totalCount}
                                        page={customers.length ? page : 0}
                                        rowsPerPage={pageSize}
                                        rowsPerPageOptions={[10, 25, 50, 100]}
                                        onChangePage={handlePageChange}
                                        onChangeRowsPerPage={event => { props.onChangeRowsPerPage(event); handlePageSizeChange(event); } }
                                    />
                                );
                            }
                        }}
                    />
                </div>
            </React.Fragment>
        );
    };

    return renderFullTable();
};

CustomersTable.defaultProps = {
    customers:        [],
    customersLoading: false,
    page:             0,
    pageSize:         100
};

CustomersTable.propTypes = {
    customers:        array.isRequired,
    customersLoading: bool,
    page:             number,
    pageSize:         number
};

export default CustomersTable;
